import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentKindBadge } from '@/features/invoices/DocumentKindBadge';
import { formatDateTime, formatMoney } from '@/features/invoices/format';
import type { DraftSummary } from './api';

/** A draft's gross total for the active locale, or the "no amount yet" placeholder when unpriced. */
function grossLabel(lng: string, draft: DraftSummary, fallback: string): string {
  return draft.grossTotal != null ? formatMoney(lng, Number(draft.grossTotal), 'PLN') : fallback;
}

/**
 * A denormalized net/VAT total for the active locale, or an em dash when the draft is too incomplete to
 * have one yet. Drafts carry no currency column (they're PLN-only in v1, like the gross total above).
 */
function moneyOrDash(lng: string, amount: string | null): string {
  return amount != null ? formatMoney(lng, Number(amount), 'PLN') : '—';
}

/** Fraction of the loaded list scrolled past before the next page is prefetched (mirrors InvoiceTable). */
const PREFETCH_AT = 0.85;
const prefetchIndex = (count: number) => Math.floor(count * PREFETCH_AT);

/**
 * Load-more wiring driven by the infinite-scroll sentinel. All optional so the components render fine
 * without pagination (e.g. in isolation tests) — the sentinel is simply not shown.
 */
interface LoadMore {
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

interface DraftListProps extends LoadMore {
  items: DraftSummary[];
  lng: string;
  /** Open the draft in the composer. */
  onOpen: (id: number) => void;
  /** Delete the draft (already confirmed by the row's dialog). */
  onDelete: (draft: DraftSummary) => void;
}

/** The delete affordance shared by the table and card list: a confirm dialog over a trash button. */
function DeleteDraftButton({ onDelete }: { onDelete: () => void }) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      title={t('invoices.drafts.confirm.delete_title')}
      description={t('invoices.drafts.confirm.delete_description')}
      confirmLabel={t('invoices.drafts.delete')}
      destructive
      onConfirm={onDelete}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0 text-destructive"
        aria-label={t('invoices.drafts.delete')}
      >
        <Trash2 className="size-4" />
      </Button>
    </ConfirmDialog>
  );
}

interface RowProps<E extends HTMLElement> {
  draft: DraftSummary;
  lng: string;
  onOpen: (id: number) => void;
  onDelete: (draft: DraftSummary) => void;
  /** Callback ref that arms the next-page prefetch; set only on the trigger row (see prefetchIndex). */
  triggerRef?: (node: E | null) => void;
}

// Memoized so appending a page only renders the new rows (see InvoiceTable for the rationale).
const DraftTableRow = memo(function DraftTableRow({
  draft,
  lng,
  onOpen,
  onDelete,
  triggerRef,
}: RowProps<HTMLTableRowElement>) {
  const { t } = useTranslation();
  return (
    <TableRow ref={triggerRef} className="cursor-pointer" onClick={() => onOpen(draft.id)}>
      <TableCell className={cn(!draft.buyerLabel && 'text-muted-foreground')}>
        <div className="leading-tight">
          <div>{draft.buyerLabel ?? t('invoices.compose.no_buyer')}</div>
          {draft.buyerNip ? (
            <div className="text-xs text-muted-foreground">{draft.buyerNip}</div>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <DocumentKindBadge kind={draft.documentKind} taxScheme={draft.taxScheme} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDateTime(lng, draft.updatedAt)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {moneyOrDash(lng, draft.netTotal)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {moneyOrDash(lng, draft.vatTotal)}
      </TableCell>
      {/* Em dash (not the "no amount yet" copy) so an unpriced row reads "— — —" across net/VAT/gross
          consistently; the descriptive fallback stays on the mobile card where gross stands alone. */}
      <TableCell
        className={cn(
          'text-right tabular-nums',
          draft.grossTotal != null ? 'font-medium' : 'text-muted-foreground',
        )}
      >
        {moneyOrDash(lng, draft.grossTotal)}
      </TableCell>
      {/* Stop the click here so deleting a draft never also opens it. */}
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        <DeleteDraftButton onDelete={() => onDelete(draft)} />
      </TableCell>
    </TableRow>
  );
});

export function DraftsTable({
  items,
  lng,
  onOpen,
  onDelete,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: DraftListProps) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLTableRowElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(items.length);
  return (
    // Fill the height the list page hands down and scroll inside the ScrollArea; the header sticks to
    // the top of that scroll viewport so column labels stay visible.
    <Table containerClassName="min-h-0 flex-1">
      <TableHeader className="sticky top-0 z-10 bg-card">
        <TableRow>
          <TableHead>{t('invoices.columns.party')}</TableHead>
          <TableHead>{t('invoices.columns.type')}</TableHead>
          <TableHead>{t('invoices.columns.updated')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.net')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.vat')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.amount')}</TableHead>
          <TableHead className="w-0 text-right">
            <span className="sr-only">{t('invoices.columns.actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((draft, i) => (
          <DraftTableRow
            key={draft.id}
            draft={draft}
            lng={lng}
            onOpen={onOpen}
            onDelete={onDelete}
            triggerRef={i === triggerIndex ? loadMoreRef : undefined}
          />
        ))}
        {isLoadingMore ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={7} className="py-4 text-center text-sm text-muted-foreground">
              {t('invoices.drafts.loading_more')}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

// Memoized for the same reason as DraftTableRow — only newly appended rows render on a page load.
const DraftCardRow = memo(function DraftCardRow({
  draft,
  lng,
  onOpen,
  onDelete,
}: Omit<RowProps<HTMLElement>, 'triggerRef'>) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/50 focus-within:bg-muted/50">
      <button
        type="button"
        onClick={() => onOpen(draft.id)}
        className="min-w-0 flex-1 cursor-pointer text-left outline-none"
      >
        <div
          className={cn(
            'truncate text-sm font-medium',
            !draft.buyerLabel && 'text-muted-foreground',
          )}
        >
          {draft.buyerLabel ?? t('invoices.compose.no_buyer')}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {grossLabel(lng, draft, t('invoices.drafts.no_total'))} ·{' '}
          {t('invoices.drafts.updated', { time: formatDateTime(lng, draft.updatedAt) })}
        </div>
      </button>
      <DeleteDraftButton onDelete={() => onDelete(draft)} />
    </div>
  );
});

/** Small-screen alternative to the table: one compact row per draft. */
export function DraftsCardList({
  items,
  lng,
  onOpen,
  onDelete,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: DraftListProps) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLLIElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(items.length);
  return (
    <ul className="divide-y">
      {items.map((draft, i) => (
        <li key={draft.id} ref={i === triggerIndex ? loadMoreRef : undefined}>
          <DraftCardRow draft={draft} lng={lng} onOpen={onOpen} onDelete={onDelete} />
        </li>
      ))}
      {isLoadingMore ? (
        <li className="py-3 text-center text-sm text-muted-foreground">
          {t('invoices.drafts.loading_more')}
        </li>
      ) : null}
    </ul>
  );
}
